import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clientApi } from '../services/clientsApi'
import { Client, ClientCreate, ClientUpdate } from '../types/client'

// Query keys
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: () => [...clientKeys.lists()] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: number) => [...clientKeys.details(), id] as const,
  search: (query: string) => [...clientKeys.all, 'search', query] as const,
}

// List all clients
export function useClients() {
  return useQuery({
    queryKey: clientKeys.list(),
    queryFn: () => clientApi.getAll(),
  })
}

// Get single client
export function useClient(id: number) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientApi.getById(id),
    enabled: !!id,
  })
}

// Search clients
export function useSearchClients(query: string) {
  return useQuery({
    queryKey: clientKeys.search(query),
    queryFn: () => clientApi.search(query),
    enabled: query.length >= 1,
  })
}

// Create client
export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ClientCreate) => clientApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}

// Update client
export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ClientUpdate }) =>
      clientApi.update(id, data),
    onSuccess: (updatedClient: Client) => {
      queryClient.setQueryData(
        clientKeys.detail(updatedClient.id),
        updatedClient
      )
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}

// Delete client
export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => clientApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: clientKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}

// Set default client
export function useSetDefaultClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => clientApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}
